import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
    Bot, CheckCircle, XCircle, Loader2, ChevronDown, Clock,
    ArrowRight, Mail, Users, Briefcase, Filter, BarChart3,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
    pending_review: 'bg-amber-100 text-amber-700',
    executed: 'bg-emerald-100 text-emerald-700',
    approved: 'bg-blue-100 text-blue-700',
    rejected: 'bg-red-100 text-red-600',
};

const AGENT_ICONS = {
    pipeline_progression: ArrowRight,
    applicant_comms: Mail,
    crm_handoff: Briefcase,
    matching: Users,
};

const AGENT_LABELS = {
    pipeline_progression: 'Pipeline',
    applicant_comms: 'Communications',
    crm_handoff: 'CRM Handoff',
    matching: 'Matching',
};

export default function ATSAgentQueue() {
    const { getAuthHeader } = useAuth();
    const [actions, setActions] = useState([]);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('pending_review');
    const [agentFilter, setAgentFilter] = useState('');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [expandedId, setExpandedId] = useState(null);
    const [processing, setProcessing] = useState({});

    const loadActions = useCallback(async () => {
        try {
            const params = new URLSearchParams({ page, page_size: 30 });
            if (statusFilter) params.set('status', statusFilter);
            if (agentFilter) params.set('agent', agentFilter);
            const res = await axios.get(`${API}/ats/agents/actions?${params}`, { headers: getAuthHeader() });
            setActions(res.data?.data || []);
            setTotal(res.data?.total || 0);
        } catch { toast.error('Failed to load agent actions'); }
        finally { setLoading(false); }
    }, [getAuthHeader, statusFilter, agentFilter, page]);

    const loadStats = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/ats/agents/stats`, { headers: getAuthHeader() });
            setStats(res.data || {});
        } catch { /* optional */ }
    }, [getAuthHeader]);

    useEffect(() => { loadActions(); loadStats(); }, [loadActions, loadStats]);

    const handleApprove = async (actionId) => {
        setProcessing(p => ({ ...p, [actionId]: 'approving' }));
        try {
            await axios.post(`${API}/ats/agents/actions/${actionId}/approve`, {}, { headers: getAuthHeader() });
            toast.success('Action approved and executed');
            loadActions();
            loadStats();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to approve'); }
        finally { setProcessing(p => ({ ...p, [actionId]: null })); }
    };

    const handleReject = async (actionId) => {
        setProcessing(p => ({ ...p, [actionId]: 'rejecting' }));
        try {
            await axios.post(`${API}/ats/agents/actions/${actionId}/reject`, { feedback: '' }, { headers: getAuthHeader() });
            toast.success('Action rejected');
            loadActions();
            loadStats();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reject'); }
        finally { setProcessing(p => ({ ...p, [actionId]: null })); }
    };

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                    AI Agent Queue
                </h1>
                <p className="text-sm text-stone-500 mt-1">Review and approve AI-suggested actions</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                    { label: 'Pending', value: stats.pending_review || 0, color: 'text-amber-600 bg-amber-50' },
                    { label: 'Executed (Auto)', value: stats.executed || 0, color: 'text-emerald-600 bg-emerald-50' },
                    { label: 'Approved', value: stats.approved || 0, color: 'text-blue-600 bg-blue-50' },
                    { label: 'Rejected', value: stats.rejected || 0, color: 'text-red-600 bg-red-50' },
                    { label: 'Total', value: stats.total || 0, color: 'text-stone-600 bg-stone-50' },
                ].map(s => (
                    <div key={s.label} className={`rounded-xl border border-stone-200 p-3 ${s.color}`}>
                        <p className="text-xl font-bold">{s.value}</p>
                        <p className="text-xs opacity-80">{s.label}</p>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3">
                <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                    className="px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10">
                    <option value="">All Statuses</option>
                    <option value="pending_review">Pending Review</option>
                    <option value="executed">Auto-Executed</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                </select>
                <select value={agentFilter} onChange={e => { setAgentFilter(e.target.value); setPage(1); }}
                    className="px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10">
                    <option value="">All Agents</option>
                    <option value="pipeline_progression">Pipeline Progression</option>
                    <option value="applicant_comms">Communications</option>
                    <option value="crm_handoff">CRM Handoff</option>
                    <option value="matching">Matching</option>
                </select>
                <span className="text-xs text-stone-400 ml-auto">{total} action{total !== 1 ? 's' : ''}</span>
            </div>

            {/* Action List */}
            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
                </div>
            ) : actions.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-xl border border-stone-200">
                    <Bot className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                    <p className="text-stone-500 text-sm">No agent actions{statusFilter ? ` with status "${statusFilter.replace(/_/g, ' ')}"` : ''}</p>
                    <p className="text-xs text-stone-400 mt-1">AI agents will create actions here as they process applications</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {actions.map(action => {
                        const AgentIcon = AGENT_ICONS[action.agent] || Bot;
                        const expanded = expandedId === action.action_id;
                        return (
                            <div key={action.action_id} className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                                <div className="flex items-center gap-3 p-4 cursor-pointer hover:bg-stone-50 transition-colors"
                                    onClick={() => setExpandedId(expanded ? null : action.action_id)}>
                                    <div className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0">
                                        <AgentIcon className="w-4 h-4 text-stone-500" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-stone-900">{action.action_type?.replace(/_/g, ' ')}</span>
                                            <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLORS[action.status] || ''}`}>
                                                {action.status?.replace(/_/g, ' ')}
                                            </span>
                                        </div>
                                        <p className="text-xs text-stone-500 mt-0.5 truncate">{action.reasoning}</p>
                                    </div>
                                    <span className="text-[10px] text-stone-400 flex items-center gap-1 flex-shrink-0">
                                        <Clock className="w-3 h-3" />
                                        {action.created_at ? new Date(action.created_at).toLocaleString() : '—'}
                                    </span>
                                    <span className="text-xs text-stone-400 bg-stone-50 px-1.5 py-0.5 rounded flex-shrink-0">
                                        {AGENT_LABELS[action.agent] || action.agent}
                                    </span>
                                    <ChevronDown className={`w-4 h-4 text-stone-300 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                                </div>

                                {expanded && (
                                    <div className="border-t border-stone-100 p-4 bg-stone-50/50">
                                        <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                                            <div>
                                                <p className="text-xs text-stone-400 uppercase tracking-wider mb-1">Context</p>
                                                <pre className="bg-white rounded-lg p-3 text-xs text-stone-600 overflow-auto max-h-40 border border-stone-200">
                                                    {JSON.stringify(action.context, null, 2)}
                                                </pre>
                                            </div>
                                            <div>
                                                <p className="text-xs text-stone-400 uppercase tracking-wider mb-1">Proposed Action</p>
                                                <pre className="bg-white rounded-lg p-3 text-xs text-stone-600 overflow-auto max-h-40 border border-stone-200">
                                                    {JSON.stringify(action.result, null, 2)}
                                                </pre>
                                            </div>
                                        </div>
                                        {action.reasoning && (
                                            <div className="mb-4">
                                                <p className="text-xs text-stone-400 uppercase tracking-wider mb-1">AI Reasoning</p>
                                                <p className="text-sm text-stone-700 bg-white rounded-lg p-3 border border-stone-200">{action.reasoning}</p>
                                            </div>
                                        )}
                                        {action.status === 'pending_review' && (
                                            <div className="flex gap-2">
                                                <Button onClick={() => handleApprove(action.action_id)}
                                                    disabled={!!processing[action.action_id]}
                                                    className="bg-emerald-600 text-white hover:bg-emerald-700" size="sm">
                                                    {processing[action.action_id] === 'approving'
                                                        ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Approving...</>
                                                        : <><CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve & Execute</>}
                                                </Button>
                                                <Button onClick={() => handleReject(action.action_id)}
                                                    disabled={!!processing[action.action_id]}
                                                    variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50">
                                                    {processing[action.action_id] === 'rejecting'
                                                        ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Rejecting...</>
                                                        : <><XCircle className="w-3.5 h-3.5 mr-1" /> Reject</>}
                                                </Button>
                                            </div>
                                        )}
                                        {action.reviewer_feedback && (
                                            <p className="text-xs text-stone-500 mt-2">Feedback: {action.reviewer_feedback}</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
